import { DashboardLayoutContainer } from 'components/Layouts/DashboardLayout';
import { Box } from '@mui/material';
import { Grid, Tab } from '@leapeasy/ui-kit';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import React, { useCallback, useEffect } from 'react';

import { getBuildingDetailAction } from 'store/actions/propertyActions';

import { openDetails } from 'store/actions/uiActions';
import { DetailCard } from './DetailCard';
import {
  ReportTab,
  ApplicationTab,
  PolicyTab,
  ClaimTab,
  InvoiceTab,
  CancellationsTab,
  DetailsTab,
} from './Tabs';
import { useDetailRouteTabs, usePropertyDetailMarkerBasePath } from 'hooks/useDetailRouteTabs';
import { DetailPageShell, SectionPanelSkeleton, TabBarSkeleton } from 'components/DetailPageState';
import { useDetailPageState } from 'hooks/useDetailPageState';
import { useImmutableJs } from 'hooks/useImmutableJs';
import { filterFlaggedCancellationDetailTabs } from 'utils/flaggedCancellationUi';

const USER_BUILDING_TAB_SLUGS = [
  'report',
  'applications',
  'policy',
  'claim',
  'invoice',
  'details',
  'flagged-cancellation',
];

const TABS = [
  {
    title: 'Report',
    icon: 'General.Report',
  },
  {
    title: 'Applications',
    icon: 'HomePlaces.MapsHomeWork',
  },
  {
    title: 'Policy',
    icon: 'FileFolder.FindInPage',
  },
  {
    title: 'Claim',
    icon: 'HomePlaces.Gite',
  },
  {
    title: 'Invoice',
    icon: 'FileFolder.Receipt',
  },
  {
    title: 'Details',
    icon: 'FileFolder.Plagiarism',
  },
  {
    title: 'Flagged Cancellation',
    icon: 'General.Flag',
  },
];

const { tabSlugs: USER_BUILDING_TAB_SLUGS_VISIBLE, tabs: USER_BUILDING_TABS } =
  filterFlaggedCancellationDetailTabs(USER_BUILDING_TAB_SLUGS, TABS);

export const DetailBuilding = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { buildingId } = useParams();

  const basePath = usePropertyDetailMarkerBasePath(buildingId);
  const { selectedIndex: selectedTab, goToTab } = useDetailRouteTabs({
    basePath,
    tabSlugs: USER_BUILDING_TAB_SLUGS_VISIBLE,
  });

  const isDemo = useSelector((state) => state.getIn(['ui', 'demo']));
  const details = useSelector((state) => state.getIn(['ui', 'details'])) || null;
  const detailsJSON = useImmutableJs(details);
  const buildingDetail =
    useSelector((state) => state.getIn(['property', 'building-detail'])) || null;
  const buildingDetailJSON = useImmutableJs(buildingDetail);
  const hasUiFallback = !buildingId && detailsJSON?.type === 'property' && detailsJSON?.data;
  const {
    data: building,
    isLoading,
    hasError,
  } = useDetailPageState({
    routeId: buildingId,
    detailRecord: buildingDetailJSON,
    hasUiFallback,
    fallbackData: detailsJSON?.data || null,
  });

  const onClickBack = useCallback(() => {
    dispatch(openDetails(null));
    navigate(-1);
  }, [dispatch, navigate]);

  useEffect(() => {
    if (!buildingId) return;
    dispatch(getBuildingDetailAction(buildingId));
  }, [buildingId, dispatch]);

  const onRetry = useCallback(() => {
    if (buildingId) dispatch(getBuildingDetailAction(buildingId));
  }, [buildingId, dispatch]);

  useEffect(() => {
    if (buildingId) return;
    if (!details || detailsJSON?.type !== 'property') {
      onClickBack();
    }
  }, [buildingId, details, detailsJSON, onClickBack]);

  return (
    <DashboardLayoutContainer shouldShowCard={false}>
      <DetailPageShell
        onBack={onClickBack}
        title={building?.name || null}
        isLoading={isLoading}
        hasError={hasError}
        onRetry={onRetry}
        loadingLabel="Loading property details..."
        skeletonVariant="fields"
      >
        {building ? <DetailCard building={building} isDemo={isDemo} /> : null}
      </DetailPageShell>

      {isLoading ? (
        <Box
          padding="24px 12px"
          borderRadius="12px"
          border="1px solid #EAEAEA"
          mt={1}
          sx={{ background: 'white' }}
        >
          <TabBarSkeleton tabCount={USER_BUILDING_TABS.length} />
          <SectionPanelSkeleton />
        </Box>
      ) : hasError || !building ? null : (
        <Box
          padding="24px 12px"
          borderRadius="12px"
          border="1px solid #EAEAEA"
          mt={1}
          sx={{ background: 'white' }}
        >
          <Grid>
            {USER_BUILDING_TABS.map((tabItem, index) => (
              <Tab
                key={USER_BUILDING_TAB_SLUGS_VISIBLE[index]}
                title={tabItem.title}
                icon={tabItem.icon}
                isSelected={selectedTab === index}
                onClick={() => goToTab(index)}
              />
            ))}
          </Grid>

          <Box marginTop="20px">
            {selectedTab === 0 && (
              <ReportTab building={building} onNavigateToApplications={() => goToTab(1)} />
            )}
            {selectedTab === 1 && <ApplicationTab building={building} />}
            {selectedTab === 2 && <PolicyTab building={building} />}
            {selectedTab === 3 && <ClaimTab building={building} />}
            {selectedTab === 4 && <InvoiceTab building={building} />}
            {selectedTab === 5 && <DetailsTab building={building} />}
            {selectedTab === 6 && <CancellationsTab building={building} />}
          </Box>
        </Box>
      )}
    </DashboardLayoutContainer>
  );
};
